/**
 * Custom currency switcher — auto-fetches live exchange rates and converts prices.
 */
theme.CustomCurrency = (function() {
  var STORAGE_KEY = 'custom_currency_code';
  var RATES_CACHE_KEY = 'custom_currency_rates_cache';
  var RATES_CACHE_TTL = 6 * 60 * 60 * 1000;
  var listVisibleClass = 'disclosure-list--visible';
  var commaDecimalCurrencies = 'ANG,ARS,BRL,BYN,BYR,CLF,CLP,COP,CRC,CZK,DKK,EUR,HRK,HUF,IDR,ISK,MZN,NOK,PLN,RON,RUB,SEK,TRY,UYU,VES,VND'.split(',');

  var config = null;
  var baseFormatMoney = null;
  var observer = null;
  var ratesLoaded = false;

  function isEnabled() {
    return config && config.enabled && config.currencies && config.currencies.length > 0;
  }

  function findCurrency(code) {
    if (!code || !config) return null;
    return config.currencies.find(function(c) {
      return c.code === String(code).toUpperCase();
    }) || null;
  }

  function getSelected() {
    var stored = localStorage.getItem(STORAGE_KEY);
    var currency = findCurrency(stored);
    if (currency) return currency;
    return config.currencies[0];
  }

  function getBaseCode() {
    return (config.base || config.currencies[0].code).toUpperCase();
  }

  function parseRate(rate) {
    var value = parseFloat(rate);
    return isNaN(value) ? 1 : value;
  }

  function parseCents(cents) {
    if (cents === null || cents === undefined || cents === '') return 0;
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var amount = parseInt(cents, 10);
    return isNaN(amount) ? 0 : amount;
  }

  function convertCents(cents, currency) {
    var amount = parseCents(cents);
    return Math.round(amount * parseRate(currency.rate));
  }

  var PLACEHOLDER_STANDARD = '{{amount_no_decimals}}';
  var PLACEHOLDER_COMMA_DECIMAL = '{{amount_no_decimals_with_comma_separator}}';

  function usesCommaDecimal(code) {
    return commaDecimalCurrencies.indexOf(code) !== -1;
  }

  function getPlaceholderForCurrency(code) {
    return usesCommaDecimal(code) ? PLACEHOLDER_COMMA_DECIMAL : PLACEHOLDER_STANDARD;
  }

  function getDisplayFormat(currency) {
    return currency.symbol + getPlaceholderForCurrency(currency.code);
  }

  function formatConverted(cents, format) {
    var currency = getSelected();
    var converted = convertCents(cents, currency);
    format = getDisplayFormat(currency);
    return baseFormatMoney(converted, format);
  }

  function patchFormatMoney() {
    if (!theme.Currency || baseFormatMoney) return;
    baseFormatMoney = theme.Currency.formatMoney;
    theme.Currency.formatMoney = function(cents, format) {
      if (!isEnabled()) {
        return baseFormatMoney(cents, format);
      }
      return formatConverted(cents, format);
    };
  }

  function updateElement(el) {
    var cents = el.getAttribute('data-money-cents');
    if (cents === null || cents === '') return;
    el.innerHTML = formatConverted(cents);
  }

  function updateAllPrices(root) {
    if (!isEnabled()) return;
    var scope = root || document;
    scope.querySelectorAll('[data-money-cents]').forEach(updateElement);
    updateSelectorLabels();
  }

  function getFlagCountry(code) {
    var option = document.querySelector('[data-custom-currency-option][data-value="' + code + '"]');
    return option ? option.getAttribute('data-flag-country') : '';
  }

  function updateSelectorLabels() {
    if (!isEnabled()) return;
    var currency = getSelected();
    var flagCountry = getFlagCountry(currency.code);

    document.querySelectorAll('[data-custom-currency-label]').forEach(function(label) {
      label.textContent = currency.code;
    });
    document.querySelectorAll('[data-custom-currency-flag]').forEach(function(flag) {
      if (flagCountry) {
        flag.className = 'currency-flag currency-flag--' + flagCountry;
        flag.setAttribute('data-custom-currency-flag', '');
        flag.setAttribute('aria-hidden', 'true');
      }
    });
    document.querySelectorAll('[data-custom-currency-item]').forEach(function(item) {
      item.classList.toggle('disclosure-list__item--current', item.getAttribute('data-custom-currency-item') === currency.code);
    });
  }

  function applyRates(rates) {
    var base = getBaseCode();
    config.currencies.forEach(function(currency) {
      if (currency.code === base) {
        currency.rate = 1;
      } else if (rates[currency.code] !== undefined) {
        currency.rate = rates[currency.code];
      }
    });
    ratesLoaded = true;
  }

  function getCachedRates() {
    try {
      var raw = localStorage.getItem(RATES_CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || cached.base !== getBaseCode()) return null;
      if (Date.now() - cached.timestamp > RATES_CACHE_TTL) return null;
      return cached.rates;
    } catch (e) {
      return null;
    }
  }

  function cacheRates(rates) {
    try {
      localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({
        base: getBaseCode(),
        timestamp: Date.now(),
        rates: rates
      }));
    } catch (e) {
      /* ignore quota errors */
    }
  }

  function fetchFromOpenErApi(base) {
    return fetch('https://open.er-api.com/v6/latest/' + base)
      .then(function(response) {
        if (!response.ok) throw new Error('Rate API error');
        return response.json();
      })
      .then(function(data) {
        if (data.result !== 'success' || !data.rates) throw new Error('Invalid rate data');
        return data.rates;
      });
  }

  function fetchFromCurrencyApi(base) {
    return fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/' + base.toLowerCase() + '.json')
      .then(function(response) {
        if (!response.ok) throw new Error('Fallback API error');
        return response.json();
      })
      .then(function(data) {
        var key = base.toLowerCase();
        if (!data || !data[key]) throw new Error('Invalid fallback data');
        var rates = {};
        Object.keys(data[key]).forEach(function(code) {
          rates[code.toUpperCase()] = data[key][code];
        });
        return rates;
      });
  }

  function fetchRates() {
    var base = getBaseCode();
    var cached = getCachedRates();
    if (cached) {
      applyRates(cached);
      updateAllPrices();
      return Promise.resolve(cached);
    }

    return fetchFromOpenErApi(base)
      .catch(function() {
        return fetchFromCurrencyApi(base);
      })
      .then(function(rates) {
        cacheRates(rates);
        applyRates(rates);
        updateAllPrices();
        document.dispatchEvent(new CustomEvent('customCurrencyRatesLoaded', {
          detail: { base: base, rates: rates }
        }));
        return rates;
      })
      .catch(function() {
        var staleRaw = localStorage.getItem(RATES_CACHE_KEY);
        if (staleRaw) {
          try {
            var stale = JSON.parse(staleRaw);
            if (stale && stale.rates && stale.base === base) {
              applyRates(stale.rates);
              updateAllPrices();
            }
          } catch (e) {
            /* ignore */
          }
        }
      });
  }

  function setCurrency(code) {
    var currency = findCurrency(code);
    if (!currency) return;
    localStorage.setItem(STORAGE_KEY, code);
    updateAllPrices();
    document.dispatchEvent(new CustomEvent('customCurrencyChange', {
      detail: { currency: currency }
    }));
  }

  function closeDisclosure(container) {
    var list = container.querySelector('[data-disclosure-list]');
    var toggle = container.querySelector('[data-disclosure-toggle]');
    if (list) list.classList.remove(listVisibleClass);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function closeAllDisclosures(except) {
    document.querySelectorAll('[data-disclosure-custom-currency]').forEach(function(container) {
      if (container !== except) closeDisclosure(container);
    });
  }

  function initDisclosures() {
    document.querySelectorAll('[data-disclosure-custom-currency]').forEach(function(container) {
      if (container.dataset.customCurrencyBound) return;
      container.dataset.customCurrencyBound = 'true';

      var toggle = container.querySelector('[data-disclosure-toggle]');
      var list = container.querySelector('[data-disclosure-list]');

      if (toggle && list) {
        toggle.addEventListener('click', function(e) {
          e.preventDefault();
          var isVisible = list.classList.contains(listVisibleClass);
          closeAllDisclosures(container);
          list.classList.toggle(listVisibleClass, !isVisible);
          toggle.setAttribute('aria-expanded', !isVisible ? 'true' : 'false');
        });
      }

      container.querySelectorAll('[data-custom-currency-option]').forEach(function(option) {
        option.addEventListener('click', function(e) {
          e.preventDefault();
          setCurrency(option.getAttribute('data-value'));
          closeDisclosure(container);
        });
      });
    });

    document.addEventListener('click', function(e) {
      if (!e.target.closest('[data-disclosure-custom-currency]')) {
        closeAllDisclosures();
      }
    });
  }

  function initMutationObserver() {
    if (observer || typeof MutationObserver === 'undefined') return;
    observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-money-cents]')) {
            updateElement(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('[data-money-cents]').forEach(updateElement);
          }
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    config = theme.settings.customCurrency;
    if (!isEnabled()) return;

    config.currencies.forEach(function(currency) {
      if (currency.code === getBaseCode()) {
        currency.rate = 1;
      } else {
        currency.rate = currency.rate || 1;
      }
    });

    patchFormatMoney();
    initDisclosures();
    initMutationObserver();
    updateAllPrices();
    fetchRates();
  }

  if (theme.settings && theme.settings.customCurrency && theme.settings.customCurrency.enabled) {
    config = theme.settings.customCurrency;
    patchFormatMoney();
  }

  return {
    init: init,
    setCurrency: setCurrency,
    getSelected: getSelected,
    updateAllPrices: updateAllPrices,
    fetchRates: fetchRates,
    isEnabled: isEnabled,
    ratesLoaded: function() { return ratesLoaded; }
  };
})();

document.addEventListener('DOMContentLoaded', function() {
  if (theme.CustomCurrency) {
    theme.CustomCurrency.init();
  }
});
